import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { getAuthData } from "../../utils/auth";

export default function HistorialIngresos() {
  const [vendedores, setVendedores] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [contratosFiltrados, setContratosFiltrados] = useState([]);
  const [supervisorSeleccionado, setSupervisorSeleccionado] = useState("");
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState("");
  const [filtro, setFiltro] = useState("Todos");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  const authData = getAuthData();
  const token = authData?.token;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/v1/admin/historial-ingresos-resumen`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setVendedores(data.vendedores || []);
      setContratos(data.contratos || []);
      setContratosFiltrados(data.contratos || []);
    } catch (error) {
      console.error("Error cargando datos", error);
    }
    setLoading(false);
  };

  const calcularResumenVendedores = (vendedoresList, contratosList) => {
    return vendedoresList.map((vendedor) => {
      const contratosVendedor = contratosList.filter(
        (c) => c.vendedor_id === vendedor.id
      );

      const totalFinanciados = contratosVendedor
        .filter((c) => c.modalidad_pago?.toLowerCase() === "financiado")
        .reduce((sum, c) => sum + (c.pago_inicial || 0), 0);

      const totalContados = contratosVendedor
        .filter((c) => c.modalidad_pago?.toLowerCase() === "contado" || c.modalidad_pago?.toLowerCase() === "de contado")
        .reduce((sum, c) => sum + (c.monto_total || 0), 0);

      return {
        ...vendedor,
        total_contratos: contratosVendedor.length,
        total_financiados: totalFinanciados,
        total_contados: totalContados,
        total_ingresos: totalFinanciados + totalContados,
      };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Filtrado lógico
  useEffect(() => {
    let filtrados = [...contratos];

    if (filtro !== "Todos") {
      const queryFiltro = filtro === "De Contado" ? "contado" : "financiado";
      filtrados = filtrados.filter(
        (c) => c.modalidad_pago?.toLowerCase().includes(queryFiltro)
      );
    }

    if (supervisorSeleccionado) {
      const idsVendedoresBajoSuper = vendedores
        .filter((v) => v.supervisor_id === supervisorSeleccionado)
        .map((v) => v.id);
      filtrados = filtrados.filter((c) => idsVendedoresBajoSuper.includes(c.vendedor_id));
    }

    if (vendedorSeleccionado) {
      filtrados = filtrados.filter((c) => c.vendedor_id === vendedorSeleccionado);
    }

    if (fechaInicio) {
      filtrados = filtrados.filter(c => new Date(c.fecha_creacion) >= new Date(fechaInicio));
    }
    if (fechaFin) {
      filtrados = filtrados.filter(c => new Date(c.fecha_creacion) <= new Date(fechaFin));
    }

    setContratosFiltrados(filtrados);
  }, [filtro, fechaInicio, fechaFin, supervisorSeleccionado, vendedorSeleccionado, contratos, vendedores]);

  // Cálculos de totales para el footer
  const totalContado = contratosFiltrados
    .filter((c) => c.modalidad_pago?.toLowerCase().includes("contado"))
    .reduce((sum, c) => sum + (c.monto_total || 0), 0);

  const totalFinanciado = contratosFiltrados
    .filter((c) => c.modalidad_pago?.toLowerCase() === "financiado")
    .reduce((sum, c) => sum + (c.pago_inicial || 0), 0);

  const totalMontoReal = totalContado + totalFinanciado;

  const vendedoresResumen = calcularResumenVendedores(vendedores, contratosFiltrados);

  const supervisores = vendedores.filter(v =>
    vendedores.some(sub => sub.supervisor_id === v.id)
  );

  const vendedoresDelSupervisor = supervisorSeleccionado
    ? vendedores.filter(v => v.supervisor_id === supervisorSeleccionado)
    : vendedores;

  return (
    <Box sx={{ margin: "0 auto", padding: 3, backgroundColor: "#ffffff", borderRadius: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: "Bakbak One", color: "#0A4575" }}>
          HISTORIAL DE INGRESOS (PAGOS REALES)
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Supervisor</InputLabel>
            <Select
              value={supervisorSeleccionado}
              label="Supervisor"
              onChange={(e) => { setSupervisorSeleccionado(e.target.value); setVendedorSeleccionado(""); }}
            >
              <MenuItem value="">Todos</MenuItem>
              {supervisores.map(sup => <MenuItem key={sup.id} value={sup.id}>{sup.nombre}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Vendedor</InputLabel>
            <Select
              value={vendedorSeleccionado}
              label="Vendedor"
              onChange={(e) => setVendedorSeleccionado(e.target.value)}
              disabled={!supervisorSeleccionado}
            >
              <MenuItem value="">Todos</MenuItem>
              {vendedoresDelSupervisor.map(v => <MenuItem key={v.id} value={v.id}>{v.nombre}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Modalidad</InputLabel>
            <Select value={filtro} label="Modalidad" onChange={(e) => setFiltro(e.target.value)}>
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="De Contado">De Contado</MenuItem>
              <MenuItem value="Financiado">Financiado</MenuItem>
            </Select>
          </FormControl>

          <TextField label="Inicio" type="date" size="small" InputLabelProps={{ shrink: true }} value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          <TextField label="Fin" type="date" size="small" InputLabelProps={{ shrink: true }} value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
        </Box>
      </Box>

      {/* Tabla Resumen */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#0A4575" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Vendedor</TableCell>
              <TableCell sx={{ color: "#fff" }}>Contratos</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ingreso Financiado (Iniciales)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ingreso Contado</TableCell>
              <TableCell sx={{ color: "#fff" }}>Total Ingresos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendedoresResumen.map((v) => (
              <TableRow key={v.id}>
                <TableCell>{v.nombre}</TableCell>
                <TableCell>{v.total_contratos}</TableCell>
                <TableCell>${v.total_financiados.toFixed(2)}</TableCell>
                <TableCell>${v.total_contados.toFixed(2)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>${v.total_ingresos.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Tabla Detalle Contratos */}
      <Typography variant="h6" sx={{ fontFamily: "Bakbak One", color: "#0A4575" }}>DETALLE DE PAGOS</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#0A4575" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>N° Contrato</TableCell>
              <TableCell sx={{ color: "#fff" }}>Vendedor</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cliente</TableCell>
              <TableCell sx={{ color: "#fff" }}>Fecha Pago</TableCell>
              <TableCell sx={{ color: "#fff" }}>Modalidad</TableCell>
              <TableCell sx={{ color: "#fff" }}>Monto Plan</TableCell>
              <TableCell sx={{ color: "#fff" }}>Abonado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contratosFiltrados.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.numero_contrato}</TableCell>
                <TableCell>{c.vendedor_nombre}</TableCell>
                <TableCell>{c.cliente?.nombre}</TableCell>
                <TableCell>{formatDate(c.fecha_creacion)}</TableCell>
                <TableCell>{c.modalidad_pago}</TableCell>
                <TableCell>${c.monto_total?.toFixed(2)}</TableCell>
                <TableCell sx={{ color: 'green', fontWeight: 'bold' }}>${c.pago_inicial?.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "end", gap: 4 }}>
        <Typography>Total Contado: <b>${totalContado.toFixed(2)}</b></Typography>
        <Typography>Total Financiado (Iniciales): <b>${totalFinanciado.toFixed(2)}</b></Typography>
        <Typography variant="h6" sx={{ color: "#0A4575" }}>Total Recaudado: <b>${totalMontoReal.toFixed(2)}</b></Typography>
      </Box>
    </Box>
  );
}